import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, InputNumber } from "formik-antd";
import { Integration } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { taskService } from "/app/src/services";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import { toast } from "sonner";

interface FormValues {
  limit: number;
}

/**
 * ClearTasks component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Integration} props.integration - The integration object.
 * @returns {JSX.Element} The ClearTasks component.
 */
export default function ClearTasks({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      return await taskService
        .bulkDelete(
          new URLSearchParams({
            integrationId: integration.id.toString(),
            limit: values.limit.toString(),
            orderby: "creationDate:asc",
          }),
        )
        .then((response) => {
          toast.success(
            `${response.deleted} ${t("translation:tasks_deleted")}`,
          );
        });
    },
    [integration.id, t],
  );
  const labelContent = useMemo(
    () => (
      <>
        {t("translation:number_tasks_to_clear")}
        <IconToolTip
          content={t("translation:clear_tasks_tip")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );

  const form: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ isSubmitting }) => (
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="limit" label={labelContent}>
              <InputNumber
                name="limit"
                size="large"
                min={1}
                max={10000}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <SubmitButton
              type="primary"
              size="large"
              block
              disabled={isSubmitting}
              style={{ marginTop: "30px" }}
            >
              {t("translation:clear_tasks")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [labelContent, t],
  );
  return (
    <Formik
      initialValues={{ limit: 10 }}
      validationSchema={simpleSchemaBuilder([
        { name: "limit", type: "number", required: true },
      ])}
      onSubmit={onSubmit}
      component={form}
      enableReinitialize
    />
  );
}
